import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient, getAuthToken } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CheckCircle, XCircle, Clock, ShieldCheck, RefreshCw } from "lucide-react";
import { useAuth } from "@/lib/auth";

interface Approval {
  id: string;
  organizationId: string;
  chatbotId: string;
  /** null for integration / ai_provider_key request types */
  toolId: string | null;
  toolName: string;
  requesterUserId: string;
  approverUserId: string | null;
  argsJson: string;
  status: "pending" | "approved" | "rejected";
  decisionNote: string | null;
  createdAt: string;
  decidedAt: string | null;
  expiresAt: string | null;
  conversationId: string | null;
  approverGroup: string | null;
  chatbotName: string | null;
  requesterName: string | null;
  approverName: string | null;
  /** Discriminator: "tool_action" | "integration" | "ai_provider_key" */
  requestType?: string;
  /** Human-readable subject label (integration name, provider name, etc.) */
  subjectName?: string | null;
  integrationId?: string | null;
  aiProviderKeyId?: string | null;
}

function statusBadge(status: string) {
  if (status === "pending")
    return <Badge variant="outline" className="border-amber-400 text-amber-600 bg-amber-50"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
  if (status === "approved")
    return <Badge className="bg-green-100 text-green-700 border-green-200"><CheckCircle className="w-3 h-3 mr-1" />Approved</Badge>;
  if (status === "rejected")
    return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />Rejected</Badge>;
  return <Badge variant="secondary">{status}</Badge>;
}

function fmt(dt: string | null) {
  if (!dt) return "—";
  return new Date(dt).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" });
}

function tryParseArgs(json: string) {
  try {
    const obj = JSON.parse(json);
    return Object.entries(obj)
      .map(([k, v]) => `${k}: ${JSON.stringify(v)}`)
      .join(", ") || "{}";
  } catch {
    return json.slice(0, 120);
  }
}

export default function ApprovalsPage() {
  const { toast } = useToast();
  const { user } = useAuth();
  const [showAll, setShowAll] = useState(false);
  const [rejectTarget, setRejectTarget] = useState<Approval | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [reasonError, setReasonError] = useState("");
  const [deciding, setDeciding] = useState<string | null>(null);

  const { data: approvals = [], isLoading, refetch } = useQuery<Approval[]>({
    queryKey: ["/api/approvals", showAll],
    queryFn: () =>
      // Use getAuthToken() — not localStorage directly — so impersonation sessions
      // (whose JWT lives in sessionStorage) also send the Authorization header.
      apiRequest("GET", `/api/approvals${showAll ? "?all=true" : ""}`)
        .then((r) => r.json())
        // Guard: API must return an array. If it returns null, an error object,
        // or any non-array shape, fall back to [] so .filter() never throws.
        .then((d: unknown) => (Array.isArray(d) ? d : []))
        .catch(() => [] as Approval[]),
  });

  // Invalidate all related queries so every open page reflects the updated
  // status immediately — no manual browser refresh required.
  const invalidateAfterDecision = () => {
    // Both the pending-only and the all-history views of the approval list.
    queryClient.invalidateQueries({ queryKey: ["/api/approvals"] });
    // Org-level AI integration status (ai-providers.tsx uses this key).
    queryClient.invalidateQueries({ queryKey: ["/api/organization/ai-integration"] });
    // Per-chatbot queries: knowledge lists, integration lists, AI provider keys.
    queryClient.invalidateQueries({ queryKey: ["/api/chatbots"], exact: false });
    // Org-wide integration list used by the integrations hub page.
    queryClient.invalidateQueries({ queryKey: ["/api/integrations"] });
    // Content governance tabs and summary counter in content-governance.tsx.
    queryClient.invalidateQueries({ queryKey: ["content-governance"] });
    queryClient.invalidateQueries({ queryKey: ["/api/content-governance"] });
    queryClient.invalidateQueries({ queryKey: ["content-governance-summary"] });
  };

  const approveMutation = useMutation({
    mutationFn: async (id: string) => {
      return apiRequest("POST", `/api/approvals/${id}/decide`, { decision: "approve" });
    },
    onSuccess: () => {
      toast({ description: "Approved. The request is now active." });
      // Explicitly refetch the approval list first — this guarantees the row
      // disappears immediately regardless of TanStack Query's staleTime setting.
      refetch();
      // Then invalidate all related downstream queries (integrations, knowledge, etc.)
      invalidateAfterDecision();
    },
    onError: (e: any) => {
      toast({ variant: "destructive", description: e?.message || "Failed to approve." });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: async ({ id, note }: { id: string; note: string }) => {
      return apiRequest("POST", `/api/approvals/${id}/decide`, { decision: "reject", note });
    },
    onSuccess: () => {
      toast({ description: "Request rejected." });
      setRejectTarget(null);
      setRejectReason("");
      // Explicitly refetch the approval list + invalidate all downstream queries.
      refetch();
      invalidateAfterDecision();
    },
    onError: (e: any) => {
      toast({ variant: "destructive", description: e?.message || "Failed to reject." });
    },
  });

  const handleApprove = async (a: Approval) => {
    setDeciding(a.id);
    try { await approveMutation.mutateAsync(a.id); }
    finally { setDeciding(null); }
  };

  const handleRejectSubmit = async () => {
    if (!rejectTarget) return;
    const trimmed = rejectReason.trim();
    if (!trimmed) {
      setReasonError("A reason is required to reject a request.");
      return;
    }
    setReasonError("");
    await rejectMutation.mutateAsync({ id: rejectTarget.id, note: trimmed });
  };

  const pending = approvals.filter((a) => a.status === "pending");
  const visible = showAll ? approvals : pending;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="border-b px-6 py-4 flex items-center justify-between bg-background">
        <div className="flex items-center gap-3">
          <ShieldCheck className="w-5 h-5 text-primary" />
          <div>
            <h1 className="text-xl font-semibold">Approvals Queue</h1>
            <p className="text-sm text-muted-foreground">
              Review and decide on tool actions that require human approval.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => refetch()} data-testid="button-refresh-approvals">
            <RefreshCw className="w-4 h-4 mr-1" />Refresh
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6">
        {/* Tabs: Pending / All */}
        <Tabs
          value={showAll ? "all" : "pending"}
          onValueChange={(v) => setShowAll(v === "all")}
          className="mb-4"
        >
          <TabsList>
            <TabsTrigger value="pending" data-testid="tab-pending">
              Pending
              {pending.length > 0 && (
                <Badge className="ml-2 h-5 min-w-5 text-[10px] bg-amber-500 text-white">{pending.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
          </TabsList>
        </Tabs>

        {isLoading ? (
          <div className="flex items-center justify-center h-40 text-muted-foreground text-sm">
            Loading approvals…
          </div>
        ) : visible.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-40 text-center text-muted-foreground gap-2">
            <ShieldCheck className="w-8 h-8 opacity-30" />
            <p className="text-sm">{showAll ? "No approval records found." : "No pending approvals. All clear!"}</p>
          </div>
        ) : (
          <div className="rounded-md border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Subject</TableHead>
                  <TableHead>Chatbot</TableHead>
                  <TableHead>Requested by</TableHead>
                  <TableHead>Approver Group</TableHead>
                  <TableHead>Arguments</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Expires</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visible.map((a) => {
                  const rt = a.requestType ?? "tool_action";
                  const typeLabel =
                    rt === "integration"       ? "Integration" :
                    rt === "ai_provider_key"   ? "AI Provider" :
                    rt === "ai_org_integration" ? "Org AI Provider" :
                    "Tool Action";
                  const typeBadgeClass =
                    rt === "integration"        ? "bg-violet-50 text-violet-700 border-violet-200" :
                    rt === "ai_provider_key"   ? "bg-sky-50 text-sky-700 border-sky-200" :
                    rt === "ai_org_integration" ? "bg-sky-50 text-sky-700 border-sky-200" :
                    "bg-orange-50 text-orange-700 border-orange-200";
                  const subjectDisplay = a.subjectName ?? a.toolName;
                  return (
                  <TableRow key={a.id} data-testid={`approval-row-${a.id}`}>
                    <TableCell>
                      <Badge variant="outline" className={`text-[11px] whitespace-nowrap ${typeBadgeClass}`}>
                        {typeLabel}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-medium max-w-[160px] truncate" title={subjectDisplay}>
                      {subjectDisplay}
                    </TableCell>
                    <TableCell className="max-w-[140px] truncate text-sm text-muted-foreground" title={a.chatbotName ?? a.chatbotId}>
                      {a.chatbotName ?? a.chatbotId.slice(0, 8) + "…"}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground max-w-[120px] truncate">
                      {a.requesterName ?? a.requesterUserId.slice(0, 8) + "…"}
                    </TableCell>
                    <TableCell className="text-sm">
                      {a.approverGroup ? (
                        <Badge variant="secondary" className="text-xs">{a.approverGroup}</Badge>
                      ) : (
                        <span className="text-muted-foreground text-xs">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate font-mono"
                      title={tryParseArgs(a.argsJson)}>
                      {tryParseArgs(a.argsJson)}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                      {fmt(a.createdAt)}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                      {fmt(a.expiresAt)}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        {statusBadge(a.status)}
                        {a.decisionNote && (
                          <span className="text-[11px] text-muted-foreground max-w-[140px] truncate" title={a.decisionNote}>
                            {a.decisionNote}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      {a.status === "pending" && (
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-green-400 text-green-700 hover:bg-green-50"
                            disabled={deciding === a.id || approveMutation.isPending}
                            onClick={() => handleApprove(a)}
                            data-testid={`button-approve-${a.id}`}
                          >
                            <CheckCircle className="w-3.5 h-3.5 mr-1" />Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-red-400 text-red-700 hover:bg-red-50"
                            disabled={deciding === a.id}
                            onClick={() => { setRejectTarget(a); setRejectReason(""); setReasonError(""); }}
                            data-testid={`button-reject-${a.id}`}
                          >
                            <XCircle className="w-3.5 h-3.5 mr-1" />Reject
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Reject Dialog — reason required */}
      <Dialog open={!!rejectTarget} onOpenChange={(o) => { if (!o) setRejectTarget(null); }}>
        <DialogContent data-testid="dialog-reject-approval">
          <DialogHeader>
            <DialogTitle>Reject approval request</DialogTitle>
            <DialogDescription>
              Provide a reason for rejecting <strong>{rejectTarget?.toolName}</strong>. This will be recorded and visible to the requester.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="reject-reason" className="text-sm">
              Reason <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="reject-reason"
              placeholder="Explain why this request is being rejected…"
              value={rejectReason}
              onChange={(e) => { setRejectReason(e.target.value); if (e.target.value.trim()) setReasonError(""); }}
              rows={3}
              data-testid="textarea-reject-reason"
              aria-invalid={!!reasonError}
              aria-describedby={reasonError ? "reject-reason-err" : undefined}
            />
            {reasonError && (
              <p id="reject-reason-err" className="text-xs text-destructive" role="alert">{reasonError}</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRejectTarget(null)} data-testid="button-cancel-reject">
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleRejectSubmit}
              disabled={rejectMutation.isPending}
              data-testid="button-confirm-reject"
            >
              {rejectMutation.isPending ? "Rejecting…" : "Reject request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
